import React, { useState, useRef } from 'react';
import {
  Download, Upload, Package,
  AlertCircle, Check, X, Copy, Link,
  FolderOpen, Cloud,
  FileCode, Info, Shield
} from 'lucide-react';

export interface FlowExportData {
  version: string;
  name: string;
  description?: string;
  nodes: any[];
  edges: any[];
  variables?: any[];
  metadata?: {
    createdAt: string;
    updatedAt: string;
    author?: string;
    checksum?: string;
    dependencies?: string[];
  };
}

interface FlowImportExportProps {
  currentFlow?: any;
  onImport?: (flowData: FlowExportData) => void;
  onExport?: (format: 'json' | 'yaml' | 'xml') => void;
  onShare?: (method: 'link' | 'email' | 'cloud') => void;
}

export const FlowImportExport: React.FC<FlowImportExportProps> = ({
  currentFlow,
  onImport,
  onExport,
  onShare
}) => {
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [importData, setImportData] = useState<string>('');
  const [importError, setImportError] = useState<string>('');
  const [exportFormat, setExportFormat] = useState<'json' | 'yaml' | 'xml'>('json');
  const [exportOptions, setExportOptions] = useState({
    includeVariables: true,
    includeMetadata: true,
    minify: false,
    encrypt: false
  });
  const [generatedLink, setGeneratedLink] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [shareMethod, setShareMethod] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFlowData = (data: any): { isValid: boolean; error?: string } => {
    try {
      if (!data.nodes || !Array.isArray(data.nodes)) {
        return { isValid: false, error: 'Dados inválidos: nodes não encontrados' };
      }
      if (!data.edges || !Array.isArray(data.edges)) {
        return { isValid: false, error: 'Dados inválidos: edges não encontrados' };
      }
      if (!data.version) {
        return { isValid: false, error: 'Dados inválidos: versão não especificada' };
      }
      return { isValid: true };
    } catch (error) {
      return { isValid: false, error: 'Erro ao validar dados do fluxo' };
    }
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setImportData(content);
        validateAndPreviewImport(content);
      };
      reader.readAsText(file);
    }
  };

  const validateAndPreviewImport = (content: string) => {
    try {
      const data = JSON.parse(content);
      const validation = validateFlowData(data);
      if (!validation.isValid) {
        setImportError(validation.error || 'Dados inválidos');
      } else {
        setImportError('');
      }
    } catch (error) {
      setImportError('Erro ao processar arquivo: formato JSON inválido');
    }
  };

  const handleImport = () => {
    if (importData && onImport) {
      try {
        const data = JSON.parse(importData);
        const validation = validateFlowData(data);
        if (validation.isValid) {
          onImport(data);
          setShowImportModal(false);
          setImportData('');
          setImportError('');
        }
      } catch (error) {
        setImportError('Erro ao importar fluxo');
      }
    }
  };

  const handleExport = () => {
    if (currentFlow && onExport) {
      onExport(exportFormat);
      setShowExportModal(false);
    }
  };

  const generateShareLink = () => {
    // Simular geração de link de compartilhamento
    const randomId = Math.random().toString(36).substr(2, 9);
    const link = `https://app.confereai.com/flow/shared/${randomId}`;
    setGeneratedLink(link);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#ffffff',
      color: '#374151'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #d1d5db',
        background: '#f9fafb'
      }}>
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: '14px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Package size={16} />
          Importar/Exportar Fluxo
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px'
        }}>
          <button
            onClick={() => setShowImportModal(true)}
            style={{
              padding: '12px',
              background: '#3B82F6',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Upload size={16} />
            Importar Fluxo
          </button>

          <button
            onClick={() => setShowExportModal(true)}
            disabled={!currentFlow}
            style={{
              padding: '12px',
              background: currentFlow ? '#10B981' : '#475569',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 500,
              cursor: currentFlow ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Download size={16} />
            Exportar Fluxo
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ padding: '16px' }}>
        <h4 style={{
          margin: '0 0 12px 0',
          fontSize: '12px',
          fontWeight: 600,
          color: '#6b7280'
        }}>
          Ações Rápidas
        </h4>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <button
            onClick={() => {
              setShareMethod('link');
              generateShareLink();
            }}
            disabled={!currentFlow}
            style={{
              padding: '10px',
              background: '#f9fafb',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              color: currentFlow ? '#e2e8f0' : '#64748b',
              fontSize: '12px',
              cursor: currentFlow ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textAlign: 'left'
            }}
          >
            <Link size={14} />
            <div>
              <div style={{ fontWeight: 500 }}>Gerar Link de Compartilhamento</div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                Crie um link para compartilhar este fluxo
              </div>
            </div>
          </button>

          <button
            onClick={() => onShare?.('cloud')}
            disabled={!currentFlow}
            style={{
              padding: '10px',
              background: '#f9fafb',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              color: currentFlow ? '#e2e8f0' : '#64748b',
              fontSize: '12px',
              cursor: currentFlow ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textAlign: 'left'
            }}
          >
            <Cloud size={14} />
            <div>
              <div style={{ fontWeight: 500 }}>Salvar na Nuvem</div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                Sincronize este fluxo com sua conta
              </div>
            </div>
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              padding: '10px',
              background: '#f9fafb',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              color: '#374151',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textAlign: 'left'
            }}
          >
            <FolderOpen size={14} />
            <div>
              <div style={{ fontWeight: 500 }}>Importar de Arquivo</div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                Carregar fluxo de arquivo JSON
              </div>
            </div>
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileImport}
          style={{ display: 'none' }}
        />
      </div>

      {/* Generated Link Display */}
      {generatedLink && (
        <div style={{
          margin: '0 16px 16px',
          padding: '12px',
          background: '#f9fafb',
          border: '1px solid #d1d5db',
          borderRadius: '6px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <span style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#10B981'
            }}>
              Link Gerado com Sucesso!
            </span>
            <button
              onClick={() => setGeneratedLink('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer'
              }}
            >
              <X size={14} />
            </button>
          </div>
          <div style={{
            display: 'flex',
            gap: '8px'
          }}>
            <input
              type="text"
              value={generatedLink}
              readOnly
              style={{
                flex: 1,
                padding: '6px',
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                color: '#374151',
                fontSize: '11px',
                fontFamily: 'monospace'
              }}
            />
            <button
              onClick={() => copyToClipboard(generatedLink)}
              style={{
                padding: '6px 12px',
                background: '#3B82F6',
                border: 'none',
                borderRadius: '4px',
                color: '#ffffff',
                fontSize: '11px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Copy size={12} />
              Copiar
            </button>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div style={{
        margin: '16px',
        padding: '12px',
        background: '#f9fafb',
        border: '1px solid #d1d5db',
        borderRadius: '6px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <Info size={14} color="#3B82F6" style={{ marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h4 style={{
              margin: '0 0 8px 0',
              fontSize: '11px',
              fontWeight: 600,
              color: '#374151'
            }}>
              Formatos Suportados
            </h4>
            <ul style={{
              margin: 0,
              paddingLeft: '16px',
              fontSize: '10px',
              color: '#6b7280'
            }}>
              <li>JSON - Formato padrão, mantém toda estrutura</li>
              <li>YAML - Formato legível, ideal para edição manual</li>
              <li>XML - Compatível com sistemas legados</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '600px',
            maxWidth: '90%',
            background: '#f9fafb',
            borderRadius: '12px',
            padding: '24px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '16px',
              fontWeight: 600,
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Upload size={18} />
              Importar Fluxo de Automação
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '8px'
              }}>
                Cole o código JSON do fluxo ou selecione um arquivo
              </label>
              <textarea
                value={importData}
                onChange={(e) => {
                  setImportData(e.target.value);
                  validateAndPreviewImport(e.target.value);
                }}
                placeholder='{"version": "1.0", "nodes": [...], "edges": [...]}'
                style={{
                  width: '100%',
                  height: '200px',
                  padding: '12px',
                  background: '#ffffff',
                  border: `1px solid ${importError ? '#EF4444' : '#d1d5db'}`,
                  borderRadius: '6px',
                  color: '#374151',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  outline: 'none'
                }}
              />
              {importError && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#EF4444',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <AlertCircle size={12} />
                  {importError}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: '10px 16px',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#374151',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FolderOpen size={14} />
                Escolher Arquivo
              </button>
            </div>

            {importData && !importError && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                background: '#ffffff',
                borderRadius: '6px'
              }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#10B981',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Check size={14} />
                  Fluxo Válido Detectado
                </h4>
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280'
                }}>
                  <div>Tamanho: {formatFileSize(new Blob([importData]).size)}</div>
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportData('');
                  setImportError('');
                }}
                style={{
                  padding: '8px 16px',
                  background: '#6B7280',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleImport}
                disabled={!importData || !!importError}
                style={{
                  padding: '8px 16px',
                  background: importData && !importError ? '#3B82F6' : '#475569',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: importData && !importError ? 'pointer' : 'not-allowed'
                }}
              >
                Importar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '500px',
            maxWidth: '90%',
            background: '#f9fafb',
            borderRadius: '12px',
            padding: '24px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '16px',
              fontWeight: 600,
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Download size={18} />
              Exportar Fluxo de Automação
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '8px'
              }}>
                Formato de Exportação
              </label>
              <div style={{
                display: 'flex',
                gap: '8px'
              }}>
                {(['json', 'yaml', 'xml'] as const).map(format => (
                  <button
                    key={format}
                    onClick={() => setExportFormat(format)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: exportFormat === format ? '#3B82F6' : '#f1f5f9',
                      border: `1px solid ${exportFormat === format ? '#3B82F6' : '#d1d5db'}`,
                      borderRadius: '6px',
                      color: exportFormat === format ? '#ffffff' : '#374151',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <FileCode size={16} />
                    <span style={{ textTransform: 'uppercase' }}>{format}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '8px'
              }}>
                Opções de Exportação
              </label>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '11px',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={exportOptions.includeVariables}
                    onChange={(e) => setExportOptions({
                      ...exportOptions,
                      includeVariables: e.target.checked
                    })}
                  />
                  Incluir variáveis
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '11px',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={exportOptions.includeMetadata}
                    onChange={(e) => setExportOptions({
                      ...exportOptions,
                      includeMetadata: e.target.checked
                    })}
                  />
                  Incluir metadados
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '11px',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={exportOptions.minify}
                    onChange={(e) => setExportOptions({
                      ...exportOptions,
                      minify: e.target.checked
                    })}
                  />
                  Minificar (reduzir tamanho)
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '11px',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={exportOptions.encrypt}
                    onChange={(e) => setExportOptions({
                      ...exportOptions,
                      encrypt: e.target.checked
                    })}
                  />
                  <Shield size={12} />
                  Criptografar (proteger com senha)
                </label>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowExportModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#6B7280',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleExport}
                style={{
                  padding: '8px 16px',
                  background: '#10B981',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Download size={14} />
                Exportar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowImportExport;
